import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { TreatmentCategory } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { TREATMENT_CATEGORIES_QUERY_KEY } from '@/constants/queryKeys';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { CATEGORY_COLOR_SWATCHES, DEFAULT_CATEGORY_COLOR } from '@/constants/treatmentLibrary';
import { useTreatmentCategoriesQuery } from '@/hooks/useTreatmentLibraryQueries';
import {
  useRemoveCategoryImageMutation,
  useReplaceCategoriesMutation,
  useUploadCategoryImageMutation,
} from '@/hooks/useTreatmentLibraryMutations';
import { cn } from '@/lib/utils';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { EntityImageUpload } from './EntityImageUpload';

interface EditableCategory {
  id: string | null;
  nameEn: string;
  namePl: string;
  color: string;
  treatmentCount: number;
  imageUrl: string | null;
  pendingImageFile: File | null;
  pendingImageRemoved: boolean;
}

function toRows(categories: TreatmentCategory[]): EditableCategory[] {
  return categories.map(function toRow(category) {
    return {
      id: category.id,
      nameEn: category.nameEn,
      namePl: category.namePl,
      color: category.color,
      treatmentCount: category.treatmentCount,
      imageUrl: category.imageUrl,
      pendingImageFile: null,
      pendingImageRemoved: false,
    };
  });
}

export function CategoriesEditor() {
  const { t } = useTranslation('treatmentLibrary');
  const queryClient = useQueryClient();
  const categoriesQuery = useTreatmentCategoriesQuery(true);
  const mutation = useReplaceCategoriesMutation();
  const uploadImage = useUploadCategoryImageMutation();
  const removeImage = useRemoveCategoryImageMutation();
  const [rows, setRows] = useState<EditableCategory[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(
    function syncFromServer() {
      if (categoriesQuery.data !== undefined) {
        setRows(toRows(categoriesQuery.data));
      }
    },
    [categoriesQuery.data]
  );

  function updateRow(index: number, patch: Partial<EditableCategory>) {
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index ? { ...row, ...patch } : row;
      });
    });
  }
  function removeRow(index: number) {
    setRows(function apply(previous) {
      return previous.filter(function keep(_row, rowIndex) {
        return rowIndex !== index;
      });
    });
  }
  function addRow() {
    setRows(function apply(previous) {
      const swatch = CATEGORY_COLOR_SWATCHES[previous.length % CATEGORY_COLOR_SWATCHES.length];
      const color = swatch === undefined ? DEFAULT_CATEGORY_COLOR : swatch;
      return [
        ...previous,
        {
          id: null,
          nameEn: '',
          namePl: '',
          color,
          treatmentCount: 0,
          imageUrl: null,
          pendingImageFile: null,
          pendingImageRemoved: false,
        },
      ];
    });
  }
  function selectImage(index: number, file: File) {
    updateRow(index, {
      imageUrl: URL.createObjectURL(file),
      pendingImageFile: file,
      pendingImageRemoved: false,
    });
  }
  function clearImage(index: number) {
    updateRow(index, { imageUrl: null, pendingImageFile: null, pendingImageRemoved: true });
  }
  async function save() {
    const completeRows = rows.filter(function complete(row) {
      return row.nameEn.trim().length > 0 && row.namePl.trim().length > 0;
    });
    const drafts = completeRows.map(function toDraft(row) {
      return {
        id: row.id,
        nameEn: row.nameEn.trim(),
        namePl: row.namePl.trim(),
        color: row.color,
      };
    });
    setIsSaving(true);
    try {
      const saved = await mutation.mutateAsync(drafts);
      let didChangeImage = false;
      for (let position = 0; position < completeRows.length; position += 1) {
        const row = completeRows[position];
        const savedCategory = saved[position];
        if (row === undefined || savedCategory === undefined) {
          continue;
        }
        if (row.pendingImageFile !== null) {
          await uploadImage.mutateAsync({ id: savedCategory.id, file: row.pendingImageFile });
          didChangeImage = true;
        } else if (row.pendingImageRemoved && row.id !== null) {
          await removeImage.mutateAsync(savedCategory.id);
          didChangeImage = true;
        }
      }
      if (didChangeImage) {
        await queryClient.invalidateQueries({ queryKey: TREATMENT_CATEGORIES_QUERY_KEY });
      }
      showSuccessNotification(t('categories.saved'));
    } catch (error) {
      showErrorNotification(resolveApiErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }

  if (categoriesQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }
  if (categoriesQuery.isError === true || categoriesQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          categoriesQuery.refetch();
        }}
      />
    );
  }

  return (
    <div>
      <p className="text-xs text-zoomuted flex items-center gap-2 mb-4">
        <Icon name="Info" />
        {t('categories.note')}
      </p>
      <Card>
        <div className="hidden sm:grid grid-cols-[auto_1fr_1fr_auto_auto] gap-3 px-1 mb-2">
          <span className="w-11 text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('image.alt')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('field.english')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('field.polish')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('categories.colour')}
          </span>
          <span className="w-8" />
        </div>
        <div className="space-y-2.5">
          {rows.map(function renderRow(row, index) {
            return (
              <div
                key={index}
                className="flex flex-col sm:grid sm:grid-cols-[auto_1fr_1fr_auto_auto] gap-2.5 sm:gap-3 sm:items-center border-b border-zooborder pb-3 last:border-b-0 last:pb-0 sm:border-b-0 sm:pb-0"
              >
                <EntityImageUpload
                  compact
                  imageUrl={row.imageUrl}
                  isBusy={isSaving}
                  onSelect={function pick(file) {
                    selectImage(index, file);
                  }}
                  onRemove={function drop() {
                    clearImage(index);
                  }}
                />
                <Input
                  value={row.nameEn}
                  placeholder={t('field.english')}
                  onChange={function update(changeEvent) {
                    updateRow(index, { nameEn: changeEvent.target.value });
                  }}
                />
                <Input
                  value={row.namePl}
                  placeholder={t('field.polish')}
                  onChange={function update(changeEvent) {
                    updateRow(index, { namePl: changeEvent.target.value });
                  }}
                />
                <div className="flex items-center justify-between sm:contents">
                  <ColorSelector
                    value={row.color}
                    onChange={function pick(color) {
                      updateRow(index, { color });
                    }}
                  />
                  {row.treatmentCount > 0 ? (
                    <span
                      className="w-8 h-8 flex items-center justify-center text-zoodisabledtext"
                      title={t('categories.inUse', { count: row.treatmentCount })}
                    >
                      <Icon name="Lock" />
                    </span>
                  ) : (
                    <Button
                      variant="destructiveGhost"
                      size="icon"
                      aria-label={t('categories.remove')}
                      onClick={function remove() {
                        removeRow(index);
                      }}
                      className="w-8 h-8"
                    >
                      <Icon name="Trash" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-zooborder">
          <Button
            type="button"
            variant="outline"
            size="small"
            className="w-full sm:w-auto whitespace-nowrap"
            disabled={isSaving}
            onClick={addRow}
          >
            <Icon name="Plus" /> {t('categories.add')}
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto whitespace-nowrap"
            isLoading={isSaving}
            onClick={save}
          >
            {t('categories.save')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

interface ColorSelectorProps {
  value: string;
  onChange: (color: string) => void;
}

function ColorSelector({ value, onChange }: ColorSelectorProps) {
  const { t } = useTranslation('treatmentLibrary');
  const isCustom = CATEGORY_COLOR_SWATCHES.indexOf(value) === -1;
  return (
    <div className="flex items-center gap-1.5">
      {CATEGORY_COLOR_SWATCHES.map(function renderSwatch(swatch) {
        return (
          <button
            key={swatch}
            type="button"
            aria-label={swatch}
            onClick={function pick() {
              onChange(swatch);
            }}
            style={{ backgroundColor: swatch }}
            className={cn(
              'w-6 h-6 rounded-lg border-2 transition-all',
              value === swatch ? 'border-zooindigo ring-2 ring-zoowhite ring-inset' : 'border-transparent'
            )}
          />
        );
      })}
      <label
        title={t('categories.customColour')}
        className={cn(
          'w-6 h-6 rounded-lg border-2 flex items-center justify-center cursor-pointer overflow-hidden relative',
          isCustom ? 'border-zooindigo' : 'border-zooborder'
        )}
        style={isCustom ? { backgroundColor: value } : undefined}
      >
        {isCustom ? null : (
          <Icon name="Plus" className="text-zoomuted" />
        )}
        <input
          type="color"
          value={isCustom ? value : DEFAULT_CATEGORY_COLOR}
          onChange={function pick(changeEvent) {
            onChange(changeEvent.target.value);
          }}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
    </div>
  );
}
